import { useState, useCallback, useEffect } from 'react';
import { Shield, CheckCircle, AlertCircle, Loader2, Building2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Non-dismissible ABN re-verification blocker (web).
 *
 * Shown on login to users whose stored ABN was detected as CANCELLED or could
 * not be verified against the Australian Business Register. There is no close
 * control — the user proceeds only by supplying a valid, ACTIVE ABN. The block
 * is lifted by the backend (verify-abn returns `abn_reverification_cleared`)
 * exclusively when the newly verified ABN is active; re-entering another
 * cancelled ABN keeps them blocked.
 */
const ReverifyAbnModal = ({ userId, reason, onCleared }) => {
  const [abn, setAbn] = useState('');
  const [validating, setValidating] = useState(false);
  const [abnResult, setAbnResult] = useState(null);
  const [businessNames, setBusinessNames] = useState([]);
  const [selectedBusinessName, setSelectedBusinessName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isCancelled = reason === 'cancelled';
  const title = isCancelled
    ? 'Your ABN has been detected as Cancelled'
    : 'Your ABN could not be verified';

  // iOS-safe body scroll lock while the blocker is up.
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      const top = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) window.scrollTo(0, parseInt(top || '0') * -1);
    };
  }, []);

  const formatABN = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)} ${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
    return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8, 11)}`;
  };

  const handleABNChange = (e) => {
    setAbn(formatABN(e.target.value));
    setError('');
    setAbnResult(null);
    setBusinessNames([]);
    setSelectedBusinessName('');
  };

  const handleValidateABN = useCallback(async () => {
    const abnDigits = abn.replace(/\s/g, '');
    if (abnDigits.length !== 11) {
      setError('ABN must be 11 digits');
      return;
    }
    try {
      setValidating(true);
      setError('');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/abn/validate`,
        { abn: abnDigits }
      );

      if (!response.data.is_valid) {
        setError(response.data.error || 'This ABN could not be found. Please check the number and try again.');
        return;
      }
      // Reject a still-cancelled ABN up front so the user gets clear feedback.
      if ((response.data.abn_status || '').toLowerCase() === 'cancelled') {
        setError('This ABN is also cancelled. Please enter your current, active ABN.');
        return;
      }

      setAbnResult(response.data);
      let names = response.data.business_names || [];
      if (names.length === 0 && response.data.entity_name && response.data.entity_name !== 'Unknown') {
        names = [response.data.entity_name];
      }
      setBusinessNames(names);
      if (names.length === 1) setSelectedBusinessName(names[0]);
      toast.success('ABN validated successfully');
    } catch (err) {
      console.error('ABN validation error:', err);
      setError(err.response?.data?.detail || 'Failed to validate ABN. Please check the number and try again.');
    } finally {
      setValidating(false);
    }
  }, [abn]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedBusinessName) {
      setError('Please select a business name');
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/verify-abn`,
        { abn: abn.replace(/\s/g, ''), selected_business_name: selectedBusinessName }
      );

      // The block only lifts when the backend confirms the ABN is active.
      if (response.data.success && response.data.abn_reverification_cleared) {
        const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
        user.abn = response.data.abn;
        user.business_name = response.data.business_name;
        user.gst_registered = response.data.gst_registered;
        user.abn_status = response.data.abn_status;
        user.abn_verified = true;
        user.abn_validated_new_system = true;
        user.abn_reverification_required = false;
        localStorage.setItem('shootzu_user', JSON.stringify(user));
        toast.success('ABN verified. Thanks — you\'re all set.');
        if (onCleared) onCleared(user);
      } else {
        setError('That ABN is not active. Please enter your current, active ABN.');
      }
    } catch (err) {
      console.error('ABN verification error:', err);
      setError(err.response?.data?.detail || 'Failed to verify ABN');
    } finally {
      setSubmitting(false);
    }
  }, [abn, selectedBusinessName, userId, onCleared]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[300]"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 20, overflow: 'hidden', touchAction: 'none' }}
    >
      <div
        className="w-full max-w-lg flex flex-col overflow-hidden"
        style={{
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          maxHeight: '90vh',
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          fontFamily: "'Work Sans', sans-serif",
          touchAction: 'pan-y',
        }}
      >
        {/* Header */}
        <div
          className="p-5 relative"
          style={{ background: 'linear-gradient(120deg,#572B7C,#78287C)', borderTopLeftRadius: 22, borderTopRightRadius: 22, overflow: 'hidden' }}
        >
          <img
            src="/assets/website/symbol-white.svg"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', right: -24, top: -24, width: 130, height: 130, opacity: 0.06, animation: 'szspin 80s linear infinite', pointerEvents: 'none' }}
          />
          <div className="relative flex items-center gap-3">
            <div className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,.18)' }}>
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {title}
              </h2>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,.8)' }}>
                To proceed with Shootzu, you'll need to verify your valid ABN
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5 flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div>
            <Label className="mb-2 block" style={{ color: 'rgba(255,255,255,.6)' }}>
              Australian Business Number (ABN) *
            </Label>
            <div className="flex gap-2">
              <Input
                value={abn}
                onChange={handleABNChange}
                placeholder="51 123 456 789"
                maxLength={14}
                className="flex-1 h-12 text-base"
                style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.14)', color: '#fff', fontFamily: "'Roboto Mono', monospace", letterSpacing: '.04em' }}
                disabled={abnResult !== null}
              />
              <Button
                type="button"
                onClick={handleValidateABN}
                disabled={validating || abnResult !== null || abn.replace(/\s/g, '').length !== 11}
                className="h-12 px-6 font-semibold hover:opacity-90"
                style={{ background: '#28C0D0', color: '#06121A' }}
              >
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : abnResult ? <CheckCircle className="w-4 h-4" /> : 'Validate'}
              </Button>
            </div>
            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>
              Enter your 11-digit ABN to verify with the Australian Business Register
            </p>
          </div>

          {error && (
            <div className="p-4" style={{ borderRadius: 12, background: 'rgba(185,28,28,.16)', border: '1px solid rgba(185,28,28,.4)' }}>
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#F87171' }} />
                <p className="text-sm" style={{ color: '#F87171' }}>{error}</p>
              </div>
            </div>
          )}

          {abnResult && (
            <div className="p-4 space-y-3" style={{ borderRadius: 12, background: 'rgba(103,198,180,.1)', border: '1px solid rgba(103,198,180,.34)' }}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" style={{ color: '#67C6B4' }} />
                <span className="font-semibold" style={{ color: '#67C6B4' }}>ABN Verified with ABR</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.6)' }}>ABN Status</p>
                  <Badge className="mt-1" style={{ background: '#15803D', color: '#fff' }}>{abnResult.abn_status}</Badge>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.6)' }}>GST Registered</p>
                  <Badge className="mt-1" style={{ background: abnResult.gst_registered ? '#28C0D0' : 'rgba(255,255,255,.18)', color: abnResult.gst_registered ? '#06121A' : '#fff' }}>
                    {abnResult.gst_registered ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.6)' }}>Entity Type</p>
                  <p className="text-sm font-medium mt-1" style={{ color: '#fff' }}>{abnResult.entity_type}</p>
                </div>
              </div>
            </div>
          )}

          {businessNames.length > 0 && (
            <div>
              <Label className="mb-2 block" style={{ color: 'rgba(255,255,255,.6)' }}>Select Your Business Name *</Label>
              {businessNames.length === 1 ? (
                <div className="p-4" style={{ borderRadius: 12, background: 'rgba(40,192,208,.08)', border: '1px solid rgba(40,192,208,.28)' }}>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5" style={{ color: '#28C0D0' }} />
                    <span className="font-semibold" style={{ color: '#fff' }}>{businessNames[0]}</span>
                  </div>
                </div>
              ) : (
                <select
                  value={selectedBusinessName}
                  onChange={(e) => setSelectedBusinessName(e.target.value)}
                  required
                  className="w-full h-12 px-4 text-base"
                  style={{ borderRadius: 11, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.14)', color: '#fff' }}
                >
                  <option value="" style={{ background: '#12162A' }}>-- Select Business Name --</option>
                  {businessNames.map((name, idx) => (
                    <option key={idx} value={name} style={{ background: '#12162A' }}>{name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="p-4" style={{ borderRadius: 12, background: 'rgba(180,83,9,.18)', border: '1px solid rgba(180,83,9,.4)' }}>
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#FCD34D' }} />
              <div className="text-xs">
                <p className="font-medium mb-1" style={{ color: '#FCD34D' }}>Why am I seeing this?</p>
                <ul className="list-disc list-inside space-y-1" style={{ color: 'rgba(255,255,255,.7)' }}>
                  <li>{isCancelled ? 'Your ABN on file is showing as cancelled with the ABR.' : 'Your ABN on file could not be verified with the ABR.'}</li>
                  <li>A valid, active ABN is required to comply with Australian tax regulations.</li>
                  <li>Your business name is set automatically from ABR records.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-1">
            <Button
              type="submit"
              disabled={!abnResult || !selectedBusinessName || submitting}
              className="w-full h-12 text-base font-bold disabled:opacity-50 hover:opacity-90"
              style={{ background: '#28C0D0', color: '#06121A' }}
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" />Verify ABN & Continue</>
              )}
            </Button>
          </div>

          <div className="text-center pt-1">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,.4)' }}>
              This verification is mandatory to continue using Shootzu
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReverifyAbnModal;
